/**
 * Struktur der Seitenleiste (#1397) nach den gemeinsamen Navigationsregeln:
 * feste offene Gruppen, Tagesgeschäft nach Häufigkeit, Stammdaten und
 * Einstellungen alphabetisch, „System“ zuletzt, „Hilfe“ abgesetzt.
 *
 * Reine Daten und Funktionen ohne Angular, damit die Sichtbarkeit pro Rolle
 * ohne TestBed prüfbar bleibt. Die Sichtbarkeit (`claim`) ist pro Eintrag
 * unverändert gegenüber der alten Leiste.
 */

export type NavBadgeKey = 'alterations' | 'duplicates' | 'export' | 'tickets';

/** Rot = dringend, Gelb = bitte ansehen. */
export type NavBadgeTone = 'red' | 'yellow';

export interface NavEntry {
  path: string;
  label: string;
  icon: string;
  claim: string;
  /** Für die Teamleitung ausblenden, obwohl sie `employee` erbt (#363). */
  hideForTeamlead?: boolean;
  badge?: { key: NavBadgeKey; tone: NavBadgeTone };
  /** Pfade, bei denen der Eintrag aktiv markiert wird (Standard: `path`). */
  activePaths?: readonly string[];
}

export interface NavGroup {
  /** `null` = Start ohne Überschrift. */
  title: string | null;
  entries: NavEntry[];
  /** Durch eine Linie abgesetzt (Hilfe). */
  separated?: boolean;
}

export interface TabLink {
  label: string;
  /** Absoluter Pfad ohne führenden Schrägstrich. */
  path: string;
  /** Bereich (Zugänge | Protokolle | …); bei Wechsel steht ein Trennstrich. */
  section?: string;
  claim?: string;
  /** Auf PROD ausblenden (Testdaten-Werkzeuge). */
  hideOnProd?: boolean;
}

/** Reiter der Seite „System“. Startseite ist das Fehlerprotokoll. */
export const SYSTEM_TABS: readonly TabLink[] = [
  { label: 'Login-Konten', path: 'admin/authUsers', section: 'Zugänge', claim: 'admin' },
  { label: 'Rollen und Rechte', path: 'admin/roles', section: 'Zugänge', claim: 'admin' },
  { label: 'Fehlerprotokoll', path: 'admin/errorLogs', section: 'Protokolle', claim: 'admin' },
  { label: 'E-Mail-Protokoll', path: 'admin/emailLogs', section: 'Protokolle', claim: 'admin' },
  { label: 'Kosten', path: 'admin/billing', section: 'Betrieb', claim: 'admin' },
  {
    label: 'Export-Einstellungen',
    path: 'admin/export-configuration',
    section: 'Betrieb',
    claim: 'admin',
  },
  { label: 'Chat-Assistent', path: 'admin/chat', section: 'Werkzeuge', claim: 'admin' },
  {
    label: 'Testdaten erzeugen',
    path: 'admin/test-data',
    section: 'Werkzeuge',
    claim: 'admin',
    hideOnProd: true,
  },
];

export const SYSTEM_START_PATH = 'admin/errorLogs';

export const LEGAL_TABS: readonly TabLink[] = [
  { label: 'AGB', path: 'legal/agb', claim: 'admin' },
  { label: 'Datenschutz', path: 'legal/datenschutz', claim: 'admin' },
];

export const PARENT_LETTER_TAB = { label: 'Schreiben', path: 'parent-letters' } as const;
export const TEAMLEAD_MAIL_TAB = {
  label: 'Versand an Teamleitungen',
  path: 'parent-letters/teamlead',
} as const;

export function parentLetterTabs(teamleadMailEnabled: boolean): TabLink[] {
  return [PARENT_LETTER_TAB, ...(teamleadMailEnabled ? [TEAMLEAD_MAIL_TAB] : [])];
}

/** Alte Pfade der Layout-Ebene, die auf den neuen Platz weiterleiten. */
export const APP_LEGACY_REDIRECTS: readonly { from: string; to: string }[] = [
  { from: 'agb', to: 'legal/agb' },
  { from: 'dataPrivacy', to: 'legal/datenschutz' },
];

/** Alter Pfad des Teamleitungs-Versands; gilt nur bei eingeschaltetem Feature-Flag. */
export const TEAMLEAD_MAIL_LEGACY_REDIRECT = { from: 'teamlead-mail', to: TEAMLEAD_MAIL_TAB.path };

/** Alte Pfade unter `admin/` (relativ zu `admin`), die auf den neuen Platz weiterleiten. */
export const ADMIN_LEGACY_REDIRECTS: readonly { from: string; to: string }[] = [
  { from: 'overview', to: 'errorLogs' },
  { from: 'data-population', to: 'test-data' },
  { from: 'enhanced-data-population', to: 'test-data' },
];

/** Der Teamleitungs-Versand ist ein Reiter von „Elternschreiben“, kein Menüpunkt. */
export function navGroups(): NavGroup[] {
  return [
    {
      title: null,
      entries: [{ path: 'overview', label: 'Übersicht', icon: 'assessment', claim: 'employee' }],
    },
    {
      title: 'Tagesgeschäft',
      entries: [
        { path: 'dashboard', label: 'Schüler', icon: 'school', claim: 'employee' },
        {
          path: 'alterations',
          label: 'Vertragsänderungen',
          icon: 'edit_document',
          claim: 'manager',
          badge: { key: 'alterations', tone: 'yellow' },
        },
        { path: 'holiday-care', label: 'Ferienbetreuung', icon: 'beach_access', claim: 'employee' },
        {
          path: 'tickets',
          label: 'Tickets',
          icon: 'inbox',
          claim: 'employee',
          badge: { key: 'tickets', tone: 'red' },
        },
        {
          path: 'duplicates',
          label: 'Doubletten',
          icon: 'content_copy',
          claim: 'manager',
          badge: { key: 'duplicates', tone: 'yellow' },
        },
        {
          path: 'export',
          label: 'Export',
          icon: 'download',
          claim: 'manager',
          // Zählt offene (noch nicht exportierte) Exporte.
          badge: { key: 'export', tone: 'yellow' },
        },
      ],
    },
    {
      title: 'Elternkommunikation',
      entries: [
        { path: 'parent-letters', label: 'Elternschreiben', icon: 'mail', claim: 'admin' },
        { path: 'parent-notices', label: 'Eltern-Hinweise', icon: 'campaign', claim: 'admin' },
      ],
    },
    {
      title: 'Stammdaten',
      entries: [
        { path: 'users', label: 'Benutzer', icon: 'group', claim: 'manager' },
        { path: 'employees', label: 'Mitarbeiter', icon: 'badge', claim: 'manager' },
        { path: 'schools', label: 'Schulen', icon: 'apartment', claim: 'manager' },
        { path: 'holiday-care-groups', label: 'Schulverbünde', icon: 'hub', claim: 'manager' },
      ],
    },
    {
      title: 'Einstellungen',
      entries: [
        {
          path: 'legal',
          label: 'Rechtstexte',
          icon: 'gavel',
          claim: 'admin',
        },
        {
          path: 'admin/ticket-notifications',
          label: 'Ticket-Benachrichtigungen',
          icon: 'notifications',
          claim: 'admin',
        },
        {
          path: 'knowledgebase',
          label: 'Wissensdatenbank',
          icon: 'psychology',
          claim: 'manager',
        },
        {
          path: SYSTEM_START_PATH,
          label: 'System',
          icon: 'admin_panel_settings',
          claim: 'admin',
          activePaths: SYSTEM_TABS.map((tab) => tab.path),
        },
      ],
    },
    {
      title: null,
      separated: true,
      entries: [
        {
          path: 'manuals',
          label: 'Hilfe & Handbücher',
          icon: 'help',
          claim: 'employee',
        },
      ],
    },
  ];
}

export interface VisibilityContext {
  hasClaim: (claim: string) => boolean;
  isTeamlead: boolean;
}

export function isEntryVisible(entry: NavEntry, ctx: VisibilityContext): boolean {
  if (entry.hideForTeamlead && ctx.isTeamlead) return false;
  return ctx.hasClaim(entry.claim);
}

/** Gruppen mit nur den Einträgen, die die Rolle sieht; leere Gruppen fallen weg. */
export function visibleNavGroups(ctx: VisibilityContext): NavGroup[] {
  return navGroups()
    .map((group) => ({ ...group, entries: group.entries.filter((e) => isEntryVisible(e, ctx)) }))
    .filter((group) => group.entries.length > 0);
}

export function visibleTabs(
  tabs: readonly TabLink[],
  hasClaim: (claim: string) => boolean,
  tier: string
): TabLink[] {
  return tabs.filter(
    (tab) => (!tab.claim || hasClaim(tab.claim)) && !(tab.hideOnProd && tier === 'PROD')
  );
}

/** Gehört die Adresse (ohne Query/Anker) zu einer der Seiten? */
export function matchesPath(url: string, paths: readonly string[]): boolean {
  const clean = url.split(/[?#]/)[0].replace(/^\/+/, '').replace(/\/+$/, '');
  return paths.some((p) => clean === p || clean.startsWith(p + '/'));
}
